import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff, Lock, Mail } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '';

export function SuperAdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/super-admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Credenciais inválidas');
        return;
      }

      const data = await response.json();
      localStorage.setItem('superAdminToken', data.accessToken);
      localStorage.setItem('superAdminRefreshToken', data.refreshToken);
      localStorage.setItem('superAdminUser', JSON.stringify(data.user));
      navigate('/super-admin/dashboard');
    } catch {
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex min-h-screen"
      style={{ background: '#0f172a' }}
    >
      {/* Left Panel — Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, #10b981 1px, transparent 0)`,
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        {/* Glow Effect */}
        <div
          className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-20 blur-[100px]"
          style={{ background: '#10b981' }}
        />

        {/* Logo + Title */}
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: '#10b981' }}
            >
              <Shield className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold text-white">FluxoPDV</span>
          </div>
          <p className="text-sm mt-1" style={{ color: '#64748b' }}>Painel Administrativo</p>
        </div>

        {/* Center Content */}
        <div className="relative">
          <h2 className="text-4xl font-bold leading-tight" style={{ color: '#f1f5f9' }}>
            Gerencie sua
            <br />
            <span style={{ color: '#10b981' }}>plataforma</span>
            <br />
            com controle total.
          </h2>
          <p className="mt-6 text-lg leading-relaxed max-w-md" style={{ color: '#94a3b8' }}>
            Acompanhe métricas, gerencie estabelecimentos, controle assinaturas e mantenha sua base de clientes crescendo.
          </p>

          {/* Stats */}
          <div className="mt-10 grid grid-cols-3 gap-6">
            <div>
              <p className="text-2xl font-bold" style={{ color: '#10b981' }}>99.9%</p>
              <p className="text-sm mt-1" style={{ color: '#64748b' }}>Uptime</p>
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: '#3b82f6' }}>24/7</p>
              <p className="text-sm mt-1" style={{ color: '#64748b' }}>Monitoramento</p>
            </div>
            <div>
              <p className="text-2xl font-bold" style={{ color: '#f59e0b' }}>Multi</p>
              <p className="text-sm mt-1" style={{ color: '#64748b' }}>Tenant</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative">
          <p className="text-sm" style={{ color: '#475569' }}>
            © {new Date().getFullYear()} FluxoPDV — Todos os direitos reservados
          </p>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10 justify-center">
            <div
              className="flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: '#10b981' }}
            >
              <Shield className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold text-white">FluxoPDV</span>
          </div>

          {/* Form Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold" style={{ color: '#f1f5f9' }}>
              Bem-vindo de volta
            </h1>
            <p className="text-base mt-2" style={{ color: '#94a3b8' }}>
              Acesse o painel de controle da plataforma
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-sm font-medium mb-2 block" style={{ color: '#94a3b8' }}>
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: '#475569' }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl pl-12 pr-4 py-3.5 text-base outline-none transition-all focus:ring-2 focus:ring-emerald-500/40"
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0' }}
                  placeholder="seu@email.com"
                  required
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block" style={{ color: '#94a3b8' }}>
                Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: '#475569' }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-xl pl-12 pr-12 py-3.5 text-base outline-none transition-all focus:ring-2 focus:ring-emerald-500/40"
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0' }}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 transition-colors hover:opacity-80"
                  style={{ color: '#64748b' }}
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="flex items-center gap-3 rounded-xl px-4 py-3"
                style={{ background: '#ef444415', border: '1px solid #ef444430' }}
              >
                <div className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: '#ef4444' }} />
                <p className="text-sm font-medium" style={{ color: '#ef4444' }}>{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl py-3.5 text-base font-semibold text-white transition-all hover:opacity-90 hover:shadow-lg disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)',
              }}
            >
              {loading ? 'Entrando...' : 'Entrar no Painel'}
            </button>
          </form>

          {/* Security Note */}
          <div className="mt-8 flex items-center gap-2 justify-center">
            <Lock className="h-3.5 w-3.5" style={{ color: '#475569' }} />
            <p className="text-xs" style={{ color: '#475569' }}>
              Conexão segura • Acesso restrito
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
